import { useEffect, useRef } from 'react';

interface User {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  profileImageUrl: string | null;
  subscription?: {
    status: string;
    plan?: string;
    expiresAt?: string;
  };
  isSubscribed?: boolean;
  isAdmin?: boolean;
}

/**
 * Hook to track user activity via WebSocket for online status monitoring
 * Automatically sends ping messages to the server while user is active
 */
export function useActivityTracking(user: User | null) {
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user || !user.id || !user.email) {
      return;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws`;
    
    console.log('🔌 Activity tracking: Connecting to WebSocket');
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ Activity tracking: Connected');
      
      // Send initial ping
      const sendPing = () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            type: 'ping',
            userId: user.id,
            email: user.email,
            firstName: user.firstName || null,
            lastName: user.lastName || null,
          }));
        }
      };

      sendPing();

      // Send ping every 60 seconds to maintain online status
      pingIntervalRef.current = setInterval(sendPing, 60000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') {
          // Server acknowledged our ping
        }
      } catch (error) {
        console.error('Activity tracking: Message error:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ Activity tracking: WebSocket error:', error);
    };

    ws.onclose = () => {
      console.log('🔌 Activity tracking: Disconnected');
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };

    // Cleanup on unmount or user change
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      if (ws.readyState === WebSocket.OPEN) {
        // Notify server of logout
        ws.send(JSON.stringify({
          type: 'logout',
          userId: user.id,
          email: user.email,
        }));
        ws.close();
      }
    };
  }, [user?.id, user?.email, user?.firstName, user?.lastName]);
}
